import axios, { isAxiosError } from 'axios';
import client from '@api/client';
import { apiRequestUrl, isTrustedApiUrl } from '@utils/apiOrigin';

interface TriggerAuthDownloadOptions {
  /**
   * Fetch presigned URLs too, so a failed download rejects instead of being
   * handed to the browser, at the cost of buffering the file in the JS heap.
   */
  observable?: boolean;
  /** Asked for by an app's page: a refusal must not sign the user out. */
  skipAuthRedirect?: boolean;
}

/**
 * Trigger a file download.
 *
 * For presigned S3 URLs the browser navigates to the URL directly so bytes go
 * straight to disk without buffering in the JS heap. Anything served by our own
 * API needs the Bearer token — which a navigation cannot carry — so it is
 * fetched via the authenticated Axios client instead. That covers both the
 * relative paths dev proxies and the absolute `os-api` URLs the deployed
 * frontends build.
 */
export const triggerAuthDownload = async (
  url: string,
  filename: string,
  { observable = false, skipAuthRedirect = false }: TriggerAuthDownloadOptions = {},
): Promise<void> => {
  if (!isTrustedApiUrl(url) && isSafeHttpUrl(url)) {
    if (observable) {
      // Bare axios, because the shared client would attach the Bearer token. Reading the
      // body relies on the artifacts bucket's CORS rule (terraform/deploy/modules/s3).
      const res = await axios.get<Blob>(url, { responseType: 'blob' });
      saveBlob(res.data, filename);
      return;
    }
    // Presigned S3 URL — native browser download, no JS heap allocation.
    const anchor = document.createElement('a');
    anchor.href = url;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    return;
  }

  const res = await client.get(apiRequestUrl(url), { responseType: 'blob', ...(skipAuthRedirect && { skipAuthRedirect }) });
  saveBlob(res.data as Blob, filename);
};

const saveBlob = (blob: Blob, filename: string): void => {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
};

export const isTransportError = (err: unknown): boolean =>
  isAxiosError(err) && !err.response;

export const isRetryableDownloadFailure = (err: unknown): boolean => {
  if (!isAxiosError(err) || !err.response) return true;
  const { status } = err.response;
  return status < 400 || status >= 500;
};

// console.error becomes a Datadog Error Tracking issue; a dropped connection should not.
export const reportDownloadFailure = (err: unknown): void => {
  if (isTransportError(err)) console.warn('Artifact download failed (network):', err);
  else console.error('Artifact download failed:', err);
};

const ARTIFACT_DOWNLOAD_PATTERN = /^\/api\/v1\/artifacts\/[^/]+\/download$/;
const ARTIFACT_DOWNLOAD_ID_PATTERN = /^\/api\/v1\/artifacts\/([^/]+)\/download$/;
const MISSING_ROOT_API_PATH_PATTERN = /^(?:\.\/)?(?=api\/)/;
const ARTIFACT_URI_PATTERN = /^(?:s3:\/\/artifacts\/|artifact:(?:\/\/)?)([^/?#]+)\/?$/i;
const BARE_ARTIFACT_ID_PATTERN =
  /^(?:art-[0-9a-f]{12}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

const isSafeHttpUrl = (url: string): boolean => {
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

export const isArtifactDownloadUrl = (href: string): boolean => {
  try {
    const url = new URL(href, globalThis.location.origin);
    return (
      url.origin === globalThis.location.origin &&
      ARTIFACT_DOWNLOAD_PATTERN.test(url.pathname)
    );
  } catch {
    return ARTIFACT_DOWNLOAD_PATTERN.test(href);
  }
};

const safeDecode = (segment: string): string | null => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
};

const canonicalArtifactPath = (id: string | null): string | null =>
  id ? `/api/v1/artifacts/${encodeURIComponent(id)}/download` : null;

const resolveAgainstSiteRoot = (href: string): URL | null => {
  try {
    return new URL(href, globalThis.location.origin);
  } catch {
    return null;
  }
};

const artifactIdFromApiUrl = (href: string): string | null => {
  const url = resolveAgainstSiteRoot(href.replace(MISSING_ROOT_API_PATH_PATTERN, '/'));
  if (!url || !isTrustedApiUrl(url.href)) return null;
  const match = ARTIFACT_DOWNLOAD_ID_PATTERN.exec(url.pathname);
  return match ? safeDecode(match[1]) : null;
};

/** The download path for any way a model spells a link to an artifact, or null if it is not one. */
export const artifactDownloadPath = (rawHref: string): string | null => {
  const href = rawHref.trim();
  if (!href) return null;
  if (BARE_ARTIFACT_ID_PATTERN.test(href)) return canonicalArtifactPath(href);
  const uriMatch = ARTIFACT_URI_PATTERN.exec(href);
  if (uriMatch) return canonicalArtifactPath(safeDecode(uriMatch[1]));
  return canonicalArtifactPath(artifactIdFromApiUrl(href));
};

/** The stored name of the artifact behind a download path, or null when it cannot be read. */
export const fetchArtifactName = async (downloadPath: string): Promise<string | null> => {
  const id = ARTIFACT_DOWNLOAD_ID_PATTERN.exec(downloadPath)?.[1];
  if (!id) return null;
  try {
    const { data } = await client.get<{ name?: unknown }>(`/api/v1/artifacts/${id}`);
    return typeof data?.name === 'string' && data.name ? data.name : null;
  } catch {
    return null;
  }
};
